/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, FolderSimple, FileText, BookOpen, Scroll, BookBookmark, Scales } from "@phosphor-icons/react";

const KIND_ICON = {
    matter: FolderSimple,
    document: FileText,
    memo: BookOpen,
    draft: Scroll,
    clause: BookBookmark,
    precedent: Scales,
};
const KIND_LABEL = {
    matters: "Matters", documents: "Documents", memos: "Research", drafts: "Drafts", clauses: "Clauses", precedents: "Precedents",
};

export default function GlobalSearch({ open, onOpenChange }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);
    const debRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) { setQ(""); setResults(null); return; }
    }, [open]);

    useEffect(() => {
        if (debRef.current) clearTimeout(debRef.current);
        if (q.trim().length < 2) { setResults(null); return; }
        debRef.current = setTimeout(async () => {
            setBusy(true);
            try {
                const r = await api.get("/search", { params: { q } }).then((r) => r.data);
                setResults(r);
            } catch (e) { setResults({ groups: {}, total: 0 }); }
            setBusy(false);
        }, 250);
        return () => clearTimeout(debRef.current);
    }, [q]);

    const open_ = (url) => { onOpenChange(false); navigate(url); };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden">
                <DialogTitle className="sr-only">Global search</DialogTitle>
                <DialogDescription className="sr-only">Search across matters, documents, research memos, drafts, clauses and precedents.</DialogDescription>
                <div className="border-b border-border px-3 py-2 flex items-center gap-2">
                    <MagnifyingGlass size={16} className="text-muted-foreground" />
                    <Input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search matters, documents, memos, drafts, clauses, precedents…"
                        className="border-none shadow-none focus-visible:ring-0"
                        data-testid="global-search-input"
                    />
                    {busy && <span className="text-xs text-muted-foreground animate-pulse">…</span>}
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="global-search-results">
                    {!results && q.trim().length < 2 && (
                        <div className="p-6 text-sm text-muted-foreground text-center">Start typing to search across everything.</div>
                    )}
                    {results && results.total === 0 && (
                        <div className="p-6 text-sm text-muted-foreground text-center">No matches for "{q}".</div>
                    )}
                    {results && results.total > 0 && (
                        <div className="space-y-3">
                            {Object.entries(results.groups).map(([kind, rows]) => {
                                if (!rows.length) return null;
                                return (
                                    <section key={kind} data-testid={`search-group-${kind}`}>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground px-2 py-1">{KIND_LABEL[kind]} · {rows.length}</div>
                                        <ul>
                                            {rows.map((r) => {
                                                const Icon = KIND_ICON[r.kind] || FileText;
                                                return (
                                                    <li key={`${r.kind}-${r.id}`}>
                                                        <button
                                                            onClick={() => open_(r.url)}
                                                            className="w-full text-left px-2 py-2 rounded-sm hover:bg-muted/60 flex items-start gap-2"
                                                            data-testid={`search-hit-${r.kind}-${r.id}`}
                                                        >
                                                            <Icon size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-medium truncate">{r.title}</div>
                                                                {r.subtitle && <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>}
                                                                {r.excerpt && (
                                                                    <div
                                                                        className="text-xs text-muted-foreground mt-0.5 line-clamp-2 [&_mark]:bg-amber-200 [&_mark]:text-foreground"
                                                                        dangerouslySetInnerHTML={{ __html: r.excerpt }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="border-t border-border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex justify-between">
                    <span>Press <kbd className="px-1 py-0.5 border border-border rounded text-[10px]">↵</kbd> to open</span>
                    <span>Cmd/Ctrl + K to toggle</span>
                </div>
            </DialogContent>
        </Dialog>
    );
}
